/**
 * Centralized Model Names Configuration
 *
 * This file contains all model name constants used throughout the application.
 * To change a model name, update it here and it will be reflected everywhere.
 */

export enum ModelName {
  RECOMMENDED = 'Recommended Model',
  GPT = 'GPT',
  CLAUDE = 'Claude',
  GEMINI = 'Gemini',
  COHERE = 'Cohere',
  GROK = 'Grok',
  MISTRAL = 'Mistral',
  DEEPSEEK = 'DeepSeek',
  NVIDIA = 'NVIDIA',
  ALIBABA = 'Alibaba',
  AI21 = 'AI21',
  GROQ = 'Groq',
  TOGETHER = 'Together',
  PERPLEXITY = 'Perplexity',
  HUGGINGFACE = 'HuggingFace',
}

export const MODEL_NAMES = {
  // UX option (resolves to one of ModelName before API usage)
  RECOMMENDED: ModelName.RECOMMENDED,
  OPENAI: ModelName.GPT,
  ANTHROPIC: ModelName.CLAUDE,
  GEMINI: ModelName.GEMINI,
  COHERE: ModelName.COHERE,
  XAI: ModelName.GROK,
  MISTRAL: ModelName.MISTRAL,
  DEEPSEEK: ModelName.DEEPSEEK,
  NVIDIA: ModelName.NVIDIA,
  ALIBABA: ModelName.ALIBABA,
  AI21: ModelName.AI21,
  GROQ: ModelName.GROQ,
  TOGETHER: ModelName.TOGETHER,
  PERPLEXITY: ModelName.PERPLEXITY,
  HUGGINGFACE: ModelName.HUGGINGFACE,
} as const;

/** Families that only appear in chat after an org enables them on LLM Keys. */
export const BYOK_CHAT_FAMILIES: readonly ModelName[] = [
  ModelName.MISTRAL,
  ModelName.DEEPSEEK,
  ModelName.NVIDIA,
  ModelName.ALIBABA,
  ModelName.AI21,
  ModelName.GROQ,
  ModelName.TOGETHER,
  ModelName.PERPLEXITY,
  ModelName.HUGGINGFACE,
];

/** Backend runtime-config / session model ids (canonical provider family names). */
export type LockedChatSessionModel =
  | 'GPT'
  | 'Gemini'
  | 'Claude'
  | 'Grok'
  | 'Cohere'
  | 'Mistral'
  | 'DeepSeek'
  | 'NVIDIA'
  | 'Alibaba'
  | 'AI21'
  | 'Groq'
  | 'Together'
  | 'Perplexity'
  | 'HuggingFace';

const LOCKED_MODEL_TO_UI_NAME: Record<LockedChatSessionModel, ModelName> = {
  GPT: ModelName.GPT,
  Gemini: ModelName.GEMINI,
  Claude: ModelName.CLAUDE,
  Grok: ModelName.GROK,
  Cohere: ModelName.COHERE,
  Mistral: ModelName.MISTRAL,
  DeepSeek: ModelName.DEEPSEEK,
  NVIDIA: ModelName.NVIDIA,
  Alibaba: ModelName.ALIBABA,
  AI21: ModelName.AI21,
  Groq: ModelName.GROQ,
  Together: ModelName.TOGETHER,
  Perplexity: ModelName.PERPLEXITY,
  HuggingFace: ModelName.HUGGINGFACE,
};

/** Map runtime-config `locked_model` to the chat UI model name. */
export function modelNameFromLockedModel(
  lockedModel: LockedChatSessionModel | null | undefined,
): ModelName | null {
  if (!lockedModel) return null;
  return LOCKED_MODEL_TO_UI_NAME[lockedModel] ?? null;
}

/**
 * Get model name by provider key
 */
export type ModelNameKey = keyof typeof MODEL_NAMES;
